"use client";

import React, { useRef, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Canvas, useFrame, extend } from "@react-three/fiber";
import { OrbitControls, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { shaderMaterial } from "@react-three/drei";
import { Wrapper, Status } from "@googlemaps/react-wrapper";

// Define the GlowMaterial for the 3D Earth model
const GlowMaterial = shaderMaterial(
  {
    map: null,
    glowCenter: new THREE.Vector3(0, 0, 0),
    glowRadius: 0.6,
    glowIntensity: 2.0,
    glowColor: new THREE.Color(0x00aaff),
    time: 0,
  },
  `varying vec2 vUv; varying vec3 vPosition; void main() { vUv = uv; vPosition = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  `uniform sampler2D map; uniform vec3 glowCenter; uniform float glowRadius; uniform float glowIntensity; uniform vec3 glowColor; uniform float time; varying vec2 vUv; varying vec3 vPosition; void main() { vec4 texColor = texture2D(map, vUv); float dist = distance(vPosition, glowCenter); float glow = smoothstep(glowRadius, 0.0, dist) * glowIntensity; glow *= (1.0 + sin(time * 2.0) * 0.3); vec3 finalColor = mix(texColor.rgb, glowColor, glow); gl_FragColor = vec4(finalColor, 1.0); }`
);

extend({ GlowMaterial });

function EarthModel() {
  const earthRef = useRef();
  const materialRef = useRef();
  const texture = useTexture("/Earth_imgFinal0.jpg", (tex, error) => {
    if (error) {
      console.error("Texture load error:", error, "Path:", "/Earth_imgFinal0.jpg");
    } else {
      console.log("Texture loaded successfully:", tex);
    }
  });

  useFrame((state, delta) => {
    if (earthRef.current) {
      earthRef.current.rotation.y += 0.003; // Slowed rotation speed
    }
    if (materialRef.current) {
      materialRef.current.time = state.clock.elapsedTime;
    }
  });

  const geometry = new THREE.SphereGeometry(1, 32, 32);
  geometry.scale(1, 1, 1);

  return (
    <group position={[3.5, 0, 0]} rotation={[0, Math.PI / 2, 0]} scale={2}>
      <mesh ref={earthRef}>
        <primitive object={geometry} />
        {texture ? (
          <glowMaterial
            ref={materialRef}
            map={texture}
            glowCenter={new THREE.Vector3(0, 0.8, 0)}
            glowRadius={0.6}
            glowIntensity={2.0}
            glowColor={new THREE.Color(0x00aaff)}
          />
        ) : (
          <meshBasicMaterial color={0x808080} />
        )}
      </mesh>
    </group>
  );
}

function EarthBackground() {
  return (
    <div className="absolute inset-0 z-2 bg-gradient-to-r from-[#00063A] via-[#425fae] to-[#00063A]">
      <Canvas className="absolute inset-0 z-5" camera={{ position: [0, 0, 1], fov: 70 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={0} />
        <EarthModel />
      </Canvas>
    </div>
  );
}

// Google Maps component
function GoogleMap() {
  const mapRef = useRef();
  const position = { lat: 28.4595, lng: 77.0266 }; // Coordinates for Gurugram, India

  useEffect(() => {
    if (mapRef.current) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: position,
        zoom: 13,
        mapTypeId: "roadmap",
      });

      new window.google.maps.Marker({
        position,
        map,
        title: "Gurugram, India",
        icon: {
          url: "/maps-and-flags.png",
          scaledSize: new window.google.maps.Size(40, 40), // width, height in pixels
        },
      });
    }
  }, []);

  return <div ref={mapRef} className="w-full h-64 md:h-96 rounded-lg" />;
}

// Render function for Google Maps Wrapper
const render = (status) => {
  switch (status) {
    case Status.LOADING:
      return <div>Loading map...</div>;
    case Status.FAILURE:
      return <div>Error loading map</div>;
    case Status.SUCCESS:
      return <GoogleMap />;
    default:
      return null;
  }
};

const Contact = () => {
  const router = useRouter();
  // Replace with your Google Maps API key
  const apiKey = "YOUR_VALID_GOOGLE_MAPS_API_KEY"; // Replace with a valid key

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    howDidYouFindUs: "",
    massage: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "phone") {
      // Remove non-numeric characters
      const numericValue = value.replace(/\D/g, "");
      
      if (numericValue.length > 10) {
        setPhoneError("Phone number cannot exceed 10 digits");
        return; // Don't update the state if exceeds 10 digits
      } else {
        setPhoneError("");
      }
      
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number before submission
    if (formData.phone && formData.phone.length > 10) {
      setSubmitError("Please enter a valid phone number (max 10 digits)");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage("");
    setSubmitError("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage("Thank you! Your message has been sent successfully.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          howDidYouFindUs: "",
          massage: "",
        });

        // Redirect to home page after 2 seconds
        setTimeout(() => {
          router.push("/");
        }, 2000);
      } else {
        setSubmitError(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setSubmitError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 md:p-6">
      {/* Background Earth */}
      <EarthBackground />

      {/* Foreground Content */}
      <div className="relative z-10 flex flex-col md:flex-row gap-4 md:gap-6 max-w-4xl w-full">
        {/* Form Section */}
        <div className="bg-blue-900 bg-opacity-0 py-4 p-4 md:p-6 mt-14 rounded-lg shadow-lg flex-1">
          <h2 className="text-white text-xl md:text-2xl font-bold mb-4">
            Get in{" "}
            <span style={{ color: "#a7c9f0" }}>Touch</span>
          </h2>

          {/* Success Message */}
          {submitMessage && (
            <div className="mb-4 p-3 bg-green-600 text-white rounded-lg">
              {submitMessage}
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-4 p-3 bg-red-600 text-white rounded-lg">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Name *"
              required
              className="w-full p-2 md:p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-sm md:text-base"
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              className="w-full p-2 md:p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-sm md:text-base"
            />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone Number *"
              required
              className={`w-full p-2 md:p-3 bg-gray-800 text-white rounded-lg border focus:outline-none text-sm md:text-base ${
                phoneError ? 'border-red-500 focus:border-red-400' : 'border-gray-600 focus:border-blue-400'
              }`}
            />
            {phoneError && (
              <div className="text-red-400 text-xs md:text-sm mt-1">
                {phoneError}
              </div>
            )}
            <select
              name="howDidYouFindUs"
              value={formData.howDidYouFindUs}
              onChange={handleInputChange}
              className="w-full p-2 md:p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-sm md:text-base"
            >
              <option value="">How did you find us?</option>
              <option value="Search Engine">Search Engine</option>
              <option value="Social Media">Social Media</option>
              <option value="Reference">Reference</option>
            </select>
            <textarea
              name="massage"
              value={formData.massage}
              onChange={handleInputChange}
              placeholder="Enter your message or inquiry here..."
              rows="4"
              className="w-full p-2 md:p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-sm md:text-base resize-none"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full p-2 md:p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm md:text-base disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send"}
            </button>
          </form>
          {/* Contact Details */}
          <div className="flex flex-wrap justify-between mt-4 text-white text-xs md:text-sm gap-2">
            <div className="flex items-center gap-2">
              <span>📞</span>
              <span>(+91) 9899709752</span>
            </div>
            <div className="flex items-center gap-2">
              <span>📧</span>
              <span>support@exponentsolutions.ai</span>
            </div>
          </div>
        </div>

        {/* Live Map Section */}
        <div className="mt-12 rounded-lg shadow-lg flex-1 flex items-center justify-center">
          <div className="w-full h-half rounded-lg overflow-hidden"
        width="70%" style={{ height: "500px" }}>
            <iframe
              title="Gurugram Google Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1350.1813229232268!2d77.05474727086232!3d28.44310881769411!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1861de9d7bb9%3A0xe403eb9336f57e45!2sUNITECH%20CYBER%20PARK%2C%20Durga%20Colony%2C%20Sector%2039%2C%20Gurugram%2C%20Haryana%20122003!5e1!3m2!1sen!2sin!4v1750845324332!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;